<template>
  <main class="w-screen h-screen flex justify-center items-center transition-all">
    <GlobalConnectivityAlert v-if="showGlobalConnectivityAlert" />
    <component :is="layout">
      <Toast position="top-right" />
      <Toast group="incoming-call-action" position="top-right">
        <template #message="slotProps">
          <div class="flex flex-col gap-2">
            <p class="text-sm font-semibold">Llamada entrante</p>
            <p class="text-xs text-(--p-surface-600) dark:text-(--p-surface-300)">
              Llamada entrante de: {{ slotProps.message.summary }}
            </p>
            <div class="flex items-center gap-2 pt-1">
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                @click="handleIncomingCallDecision('accepted')"
              >
                Aceptar
              </button>
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium bg-rose-600 text-white hover:bg-rose-700"
                @click="handleIncomingCallDecision('rejected')"
              >
                Rechazar
              </button>
            </div>
          </div>
        </template>
      </Toast>
      <router-view />
    </component>
  </main>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

import { useSeoMeta } from '@unhead/vue'
import { useI18n } from 'vue-i18n'

import AuthLayout from '@/layouts/AuthLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import GlobalConnectivityAlert from '@/components/molecules/connectivity/GlobalConnectivityAlert.vue'
import { useI18nStore } from '@/stores/useI18nStore'
import { useThemeStore } from '@/stores/useThemeStore'
import { type IncomingCallDecision } from '@/stores/useNotificationStore'
import { useAppSettingsStore } from '@/stores/useAppSettingsStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { bulkAnalysisNotificationTracker } from '@/services/ai/bulkAnalysisNotificationTracker'

export default defineComponent({
  components: {
    DefaultLayout,
    AuthLayout,
    DashboardLayout,
    GlobalConnectivityAlert,
    Toast,
  },
  setup() {
    const route = useRoute()
    const i18n = useI18n()
    const i18nStore = useI18nStore()
    const themeStore = useThemeStore()
    const authStore = useAuthStore()
    const appSettingsStore = useAppSettingsStore()
    const connectivityStore = useConnectivityStore()
    const toast = useToast()
    const unsubscribeBulkAnalysisEvents = bulkAnalysisNotificationTracker.subscribe(
      (job, eventType) => {
        if (eventType !== 'terminal') return

        const isPausedForCredits =
          job.status === 'paused' ||
          job.status === 'paused_insufficient_credits' ||
          job.error_code === 'insufficient_credits' ||
          job.paused_reason === 'insufficient_credits'
        const isFailed = job.status === 'failed'
        const isPartial = job.status === 'partial'

        toast.add({
          severity: isPausedForCredits || isPartial ? 'warn' : isFailed ? 'error' : 'success',
          summary: isPausedForCredits
            ? i18n.t('ai-reports.toasts.analysis_paused_insufficient_credits')
            : isFailed
              ? i18n.t('ai-reports.general.analysis_failed')
              : i18n.t('ai-reports.general.analysis_completed'),
          detail: isPausedForCredits
            ? i18n.t('ai-reports.toasts.analysis_paused_insufficient_credits_detail')
            : isPartial
              ? i18n.t('ai-reports.toasts.analysis_finished_incidents')
              : isFailed
                ? i18n.t('ai-reports.toasts.analysis_finished_error')
                : i18n.t('ai-reports.toasts.analysis_finished_success'),
          life: 5000,
        })
      },
    )

    const layout = computed(() => {
      const layoutName = (route.meta.layout as string) || 'DefaultLayout'
      return layoutName
    })
    const showGlobalConnectivityAlert = computed(() => route.name !== 'sign-in')

    const pageTitle = computed<string>(() => {
      const titleKey = route.meta.title as string
      const title = titleKey ? i18n.t(`${titleKey}`) : i18n.t('common.titles.home')
      const callStatusTitles = {
        calling: i18n.t('common.webphone_status.calling'),
        incoming: i18n.t('common.webphone_status.incoming'),
        connected: i18n.t('common.webphone_status.connected'),
        held: i18n.t('common.webphone_status.held'),
      } as const

      if (authStore.webphoneCallStatus !== 'idle') {
        return callStatusTitles[authStore.webphoneCallStatus]
      }

      return title
    })

    useSeoMeta({
      title: pageTitle,
      description: 'meta description',
      titleTemplate: `%s | ${i18n.t('common.titles.app_name')}`,
    })

    // Zona horaria de la instalacion (generals flag 13): se pide al quedar
    // autenticado y de ahi la usa todo el formateo de fechas.
    watch(
      () => authStore.isAuthenticated,
      (isAuthenticated) => {
        if (isAuthenticated) {
          void appSettingsStore.ensureTimeZoneLoaded()
          bulkAnalysisNotificationTracker.start()
        } else {
          bulkAnalysisNotificationTracker.stop()
        }
      },
      { immediate: true },
    )

    watch(
      () => i18nStore.language,
      (newLang) => {
        i18n.locale.value = newLang
      },
      { immediate: true },
    )

    onMounted(() => {
      themeStore.initUserTheme()
      connectivityStore.installNetworkListeners()
    })

    onUnmounted(() => {
      connectivityStore.removeNetworkListeners()
      bulkAnalysisNotificationTracker.stop()
      unsubscribeBulkAnalysisEvents()
    })

    const handleIncomingCallDecision = (decision: IncomingCallDecision = 'rejected') => {
      toast.removeGroup('incoming-call-action')
      authStore.requestIncomingCallAction(decision === 'accepted' ? 'accept' : 'reject')
    }

    return { layout, showGlobalConnectivityAlert, handleIncomingCallDecision }
  },
})
</script>
